/**
 * Canonical layout signature — the stable "fingerprint" of a layout blob used to
 * decide whether the working layout really differs from the last loaded/saved
 * one, i.e. whether the user actually edited something.
 *
 * WHY this exists: comparing OBJECT IDENTITY (or relying on a separate `dirty`
 * boolean) is not a safe dirty test for these editors. A scope switch
 * (Global template <-> This band) *reloads* the layout: `mergePdfLayouts(
 * createDefaultPdfLayout(), saved)` always hands back a BRAND NEW object, and
 * the merge can even normalise/backfill fields (the payload is not necessarily
 * byte-identical to what the server sent). With an identity/stale-flag test a
 * flush could therefore fire while the user had touched nothing — and, because
 * the flush runs in the cleanup of the `[scope, activeProfileId]` effect, it
 * persisted that untouched (or half-loaded) layout OVER the scope being left:
 * that is how the GLOBAL template got replaced by a default/band layout.
 *
 * The signature is key-order independent and cached per object identity (the
 * editors never mutate a layout in place), so calling it on every render is
 * cheap.
 */
function sortValue(value: any): any {
  if (Array.isArray(value)) return value.map(sortValue);
  if (value && typeof value === 'object') {
    const out: Record<string, any> = {};
    for (const key of Object.keys(value).sort()) {
      if (value[key] === undefined) continue; // JSON.stringify drops undefined
      out[key] = sortValue(value[key]);
    }
    return out;
  }
  return value;
}

/** JSON.stringify with recursively sorted keys (stable across key insertion order). */
export function canonicalJson(value: unknown): string {
  return JSON.stringify(sortValue(value));
}

const signatureCache = new WeakMap<object, string>();

/** Canonical signature of a layout; identical content ⇒ identical string. */
export function layoutSignature(layout: unknown): string {
  if (layout && typeof layout === 'object') {
    const cached = signatureCache.get(layout as object);
    if (cached !== undefined) return cached;
    const signature = canonicalJson(layout);
    signatureCache.set(layout as object, signature);
    return signature;
  }
  return canonicalJson(layout);
}
